
import { useState, useCallback } from 'react';
import { X, CheckCircle, Copy, Check, Download } from 'lucide-react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { toast } from 'sonner';

// =============================================================================
// Types
// =============================================================================

interface ExportAslDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    aslJson: string;
}

// =============================================================================
// Main Component
// =============================================================================

export default function ExportAslDrawer({
    isOpen,
    onClose,
    aslJson,
}: ExportAslDrawerProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(aslJson);
            setCopied(true);
            toast.success('Copied to clipboard');

            // Reset copied state after 2 seconds
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        } catch (err) {
            toast.error('Failed to copy to clipboard');
        }
    }, [aslJson]);

    const handleDownload = useCallback(() => {
        try {
            const blob = new Blob([aslJson], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'workflow-asl.json';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('Downloaded workflow-asl.json');
        } catch (err) {
            toast.error('Failed to download file');
        }
    }, [aslJson]);

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                                        <CheckCircle className="h-5 w-5 text-green-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-slate-900">
                                            Export ASL Definition
                                        </h2>
                                        <p className="text-sm text-slate-500">
                                            Amazon States Language (ASL) workflow definition
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onClose();
                                    }}
                                    className="rounded-full p-2 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Content - Code Preview */}
                        <div className="flex-1 overflow-hidden bg-slate-900 m-4 rounded-xl">
                            <pre className="h-full overflow-auto p-6 text-sm text-slate-100 font-mono leading-relaxed">
                                <code>{aslJson}</code>
                            </pre>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center">
                            <p className="text-xs text-slate-500">
                                {aslJson.split('\n').length} lines · {new Blob([aslJson]).size.toLocaleString()} bytes
                            </p>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onClose();
                                    }}
                                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:shadow-md"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDownload();
                                    }}
                                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:shadow-md"
                                >
                                    <Download size={16} />
                                    Download
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleCopy();
                                    }}
                                    className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-slate-800 hover:shadow-md"
                                >
                                    {copied ? (
                                        <>
                                            <Check size={16} />
                                            Copied!
                                        </>
                                    ) : (
                                        <>
                                            <Copy size={16} />
                                            Copy to Clipboard
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
        </SlideDrawer>
    );
}

